import React, { useContext, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, message } from '@brokerbay/design-components';
import { TourItemType } from '@bb/domain/dist/types';

import {
  AddListingToTourModalContext,
  AddListingToTourModalContextType,
  AuthContext,
  LayoutContext,
  UniversalSearchContext,
  UniversalSearchContextType,
} from 'contexts';
import { featureEnabled, SUPPORT_EMAIL } from 'utils';
import errorReporter from '@app/errorReporter';
import {
  createNewTour,
  getTours,
  addListingToTour,
  getTourContent,
} from 'api/TourController';
import { Button, Modal, theme, Icon, breakpoints } from 'components';
import { TourStopAddToTourModal } from 'api/TourController/getTours';
import { useBreakpoint, useDebounce } from 'hooks';
import {
  useHideChat,
  useTemporaryStyle,
  useWindowHeight,
} from '../../Router/pages/TourEditPage/hooks';
import ListingSummary from './ListingSummary';
import TourDetailContainer from './TourDetailContainer';
import TourList from './TourList';

import { TourItemContent } from '../../Router/pages/TourEditPage/types';
import {
  StyledFooterButton,
  StyledTooltipButtonWrapper,
} from '../../Router/pages/TourEditPage/StyledComponents';
import { MAX_TOUR_STOPS } from '../../Router/pages/TourEditPage/constants';
import MaxItemsExceededTooltip from '../../Router/pages/TourEditPage/MaxItemsExceededTooltip';

enum ModalStages {
  SELECTING_TOUR = 'SELECTING_TOUR',
  SELECTED_TOUR = 'SELECTED_TOUR',
  ADDED_TO_TOUR = 'ADDED_TO_TOUR',
}

export const AddListingToTourModal: React.FC = () => {
  const { md } = useBreakpoint();
  const height = useWindowHeight();
  const { topElementHeights } = useContext(LayoutContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const totalTopElementHeights = useMemo(
    () => Object.values(topElementHeights).reduce((sum, cur) => sum + cur),
    [topElementHeights],
  );
  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [creatingNewTour, setCreatingNewTour] = React.useState<boolean>(false);
  const [tours, setTours] = React.useState<
    {
      id: string;
      name: string;
      date: Date;
      stops: TourStopAddToTourModal[];
    }[]
  >([]);
  const [tourStops, setTourStops] = React.useState<Partial<TourItemContent>[]>(
    [],
  );
  const [selectedTourId, setSelectedTourId] = React.useState<string>();
  const [currentStage, setCurrentStage] = React.useState<ModalStages>(
    ModalStages.SELECTING_TOUR,
  );

  const { modalVisible, setModalVisible, listing, setListing } =
    useContext<AddListingToTourModalContextType>(AddListingToTourModalContext);
  const searchContext = useContext<UniversalSearchContextType>(
    UniversalSearchContext,
  );
  const { hasFeature } = useContext(AuthContext);

  const hideChat = useDebounce(modalVisible && !md, modalVisible ? 0 : 250);
  useHideChat(hideChat);

  // stop body scroll on iOS
  useTemporaryStyle(
    () => document.body,
    modalVisible && !md && { position: 'fixed' },
    [modalVisible && !md],
  );

  /**
   * For some reason, iOS safari doesn't render the content that are:
   * outside of html element && within address bar area && when the address bar hides && when the modal opens
   * overflow doesn't seem to be related to this issue. This hook fixes that; '-1' just ensures the body is not scrollable
   */
  useTemporaryStyle(
    () => $('html')[0],
    modalVisible && !md && { height: `calc(100vh - 1px)` },
    [modalVisible && !md],
  );

  React.useEffect(() => {
    if (contentRef.current) contentRef.current.parentElement.scrollTo(0, 0);
    if (!modalVisible) return;
    setCurrentStage(ModalStages.SELECTING_TOUR);
    (async () => {
      setLoaded(false);
      try {
        setTours(await getTours());
      } catch (err) {
        message.error('Error fetching tours data');
        errorReporter.error(`Failed to get list of tours: ${err}`);
      }
      setLoaded(true);
    })();
  }, [modalVisible]);

  React.useEffect(() => {
    contentRef.current?.parentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [currentStage]);

  const closeModal = () => {
    setModalVisible(false);
  };

  const goBack = () => {
    switch (currentStage) {
      case ModalStages.SELECTED_TOUR:
        setCurrentStage(ModalStages.SELECTING_TOUR);
        break;
      default:
        closeModal();
    }
  };

  const selectTour = async (tourId: string) => {
    setLoaded(false);
    setSelectedTourId(tourId);
    try {
      const { tourItems } = await getTourContent(tourId);
      if (tourItems.some(item => item.listingId === listing.id)) {
        message.warn(
          'This listing already exists in your tour. You cannot add a listing to a tour more than once.',
        );
        setLoaded(true);
        return;
      }
      setTourStops(tourItems);
      setCurrentStage(ModalStages.SELECTED_TOUR);
    } catch (err) {
      message.error('Error fetching tour data');
      errorReporter.error(`Failed to fetch tour content: ${err}`);
    }
    setLoaded(true);
  };

  const editTour = () => {
    setModalVisible(false);
    if (searchContext) searchContext.setCloseSearchFromTourModal(true);

    // Assuming Hash Routing, if on edge case, the user adds listing to
    // a tour on the tour page itself, using the top nav's universal search
    // instead of the search bar on the tour page
    // -> setting window.location.href will not refresh the page
    if (window.location.hash === `#/tours/${selectedTourId}`) {
      window.location.reload();
    } else {
      window.location.href = `/#/tours/${selectedTourId}`;
    }
  };

  const createTour = async () => {
    setCreatingNewTour(true);
    try {
      const newTourId = await createNewTour(listing.id);
      setModalVisible(false);
      if (searchContext) searchContext.setCloseSearchFromTourModal(true);

      window.location.href = `/#/tours/${newTourId}`;
    } catch (err) {
      message.error(
        <span>
          The tour could not be created. Try again in a moment or contact
          <a href={`mailto:${SUPPORT_EMAIL}`}>{` ${SUPPORT_EMAIL}`}</a>
        </span>,
      );
      errorReporter.error(`Failed to create tour: ${err}`);
    }
    setCreatingNewTour(false);
  };

  const addToTour = async () => {
    setLoaded(false);
    try {
      await addListingToTour(selectedTourId, listing.id);
      const newStops = [...tourStops];
      newStops.push({
        type: TourItemType.APPOINTMENT,
        address: `${listing.line1} ${listing.line2 || ''}`,
        thumbnail: listing.thumbnail,
        isUnclaimed: listing.isUnclaimed,
      });
      const updatedTours = [...tours];
      updatedTours.find(tourItem => tourItem.id === selectedTourId).stops =
        newStops;

      setTourStops(newStops);
      setTours(updatedTours);
      setCurrentStage(ModalStages.ADDED_TO_TOUR);
    } catch (err) {
      message.error(
        <span>
          The listing could not be added to the tour. Try again in a moment or
          contact
          <a href={`mailto:${SUPPORT_EMAIL}`}>{` ${SUPPORT_EMAIL}`}</a>
        </span>,
      );
      errorReporter.error(`Failed to add listing to tour: ${err}`);
    }
    setLoaded(true);
  };

  const getCurrentStageContent = () => {
    switch (currentStage) {
      case ModalStages.SELECTED_TOUR:
        return (
          <>
            {getPrebookingAlert()}
            <StyledSummaryHeader>
              <ListingSummary {...listing} />
            </StyledSummaryHeader>
            <TourDetailContainer
              loading={!loaded}
              tourSummary={tours.find(tour => tour.id === selectedTourId)}
              tourStops={tourStops}
            />
          </>
        );
      case ModalStages.ADDED_TO_TOUR:
        return (
          <>
            <StyledAlert
              message="This listing was added to the tour. You will be able to schedule times from within the ‘Edit Tour’ screen."
              type="success"
              showIcon
            />
            <TourDetailContainer
              loading={!loaded}
              tourSummary={tours.find(tour => tour.id === selectedTourId)}
              tourStops={tourStops}
            />
          </>
        );
      default:
        return (
          <>
            {getPrebookingAlert()}
            <StyledSummaryHeader>
              <ListingSummary {...listing} />
            </StyledSummaryHeader>
            <StyledCreateNewTourButton
              data-cy="create-new-tour-button"
              type={'default'}
              block
              onClick={createTour}
              loading={creatingNewTour}
              disabled={creatingNewTour}
            >
              <Icon
                width={18}
                type={'antdPlusOutlined'}
                color={!creatingNewTour ? 'textPrimary' : undefined}
              />
              Create New Tour
            </StyledCreateNewTourButton>
            <TourList
              tours={tours}
              onTourSelect={selectTour}
              loading={!loaded}
            />
          </>
        );
    }
  };

  const getCurrentStageFooter = () => {
    switch (currentStage) {
      case ModalStages.SELECTING_TOUR:
        return md
          ? [
              <StyledFooterButton
                data-cy="close-add-to-tour-modal-button"
                key="Close"
                type="ghost"
                onClick={closeModal}
              >
                Cancel
              </StyledFooterButton>,
            ]
          : null;
      case ModalStages.SELECTED_TOUR: {
        const tourCount = tours.find(tour => tour.id === selectedTourId).stops
          .length;
        return [
          <MaxItemsExceededTooltip key="toolTip" totalStops={tourCount}>
            <StyledTooltipButtonWrapper>
              <StyledFooterButton
                data-cy="add-listing-to-tour-button"
                disabled={tourCount >= MAX_TOUR_STOPS || !loaded}
                loading={!loaded}
                key="Add"
                type="primary"
                onClick={addToTour}
                block={!md}
              >
                Add to Tour
              </StyledFooterButton>
            </StyledTooltipButtonWrapper>
          </MaxItemsExceededTooltip>,
        ];
      }
      case ModalStages.ADDED_TO_TOUR:
        return [
          <StyledFooterWrapper $md={md} key="FooterButtons">
            <StyledFooterButton
              data-cy="close-add-to-tour-modal-button"
              type="ghost"
              onClick={closeModal}
            >
              Done
            </StyledFooterButton>
            <StyledFooterButton
              data-cy="edit-tour-button"
              type="primary"
              onClick={editTour}
            >
              Edit Tour
            </StyledFooterButton>
          </StyledFooterWrapper>,
        ];
      default:
    }
  };

  const getPrebookingAlert = () => {
    if (
      listing?.preBookingInformation &&
      featureEnabled('preBookingToursNotificationFeature') &&
      hasFeature('PRE_BOOKING')
    ) {
      return (
        <StyledAlert
          type="info"
          message={listing.preBookingInformation}
          showIcon
        />
      );
    }
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <StyledModal
        title={
          <>
            {currentStage !== ModalStages.ADDED_TO_TOUR && (
              <StyledBackButton
                data-cy="go-back-button"
                onClick={goBack}
                size={13}
              />
            )}
            Add to tour
          </>
        }
        open={modalVisible}
        afterClose={() => setListing(null)}
        onCancel={closeModal}
        footer={getCurrentStageFooter()}
        bodyStyle={{
          maxHeight: breakpoints.sm,
          ...(!md
            ? {
                maxHeight: undefined,
                height:
                  height -
                  totalTopElementHeights -
                  (currentStage === ModalStages.SELECTING_TOUR ? 55 : 108),
              }
            : {}),
        }}
        width={!md ? '100vw' : '568px'}
        $md={md}
        $topElementHeights={totalTopElementHeights}
        centered={!md}
        transitionName={!md ? 'ant-move-down' : undefined}
      >
        <div ref={contentRef}>{getCurrentStageContent()}</div>
      </StyledModal>
    </div>
  );
};

const StyledFooterWrapper = styled.div<{ $md: boolean }>`
  ${({ $md }) =>
    !$md &&
    `
      display: grid;
      grid-template-columns: auto auto;
    `}
`;

const StyledModal = styled(Modal)<{
  $md?: boolean;
  $topElementHeights?: number;
}>`
  ${({ $md, $topElementHeights }) =>
    !$md &&
    `
      position: fixed;
      left: 0px;
      top: unset;
      max-width: unset;
      margin: unset;
      padding: unset;
      .ant-modal-content {
        top: ${$topElementHeights}px;
      }
  `}

  .ant-modal-body {
    // if overlay is not supported, this will make browser use auto as fallback;
    overflow: auto;
    overflow: overlay;
  }
`;

const StyledAlert = styled(Alert)`
  box-sizing: border-box;
  border-radius: 2px;
  margin-bottom: 10px;
`;

const StyledSummaryHeader = styled.div`
  margin-bottom: 24px;
`;

const StyledBackButton = styled(ArrowLeftOutlined)`
  margin-right: 14.43px;
`;

const StyledCreateNewTourButton = styled(Button)`
  &&& {
    height: 40px;
    width: 100%;
    border-radius: 2px;
    font-weight: normal;
    font-size: 16px;
    :not(:disabled):not(:hover) {
      color: ${theme.textColor};
    }
    ${Icon} {
      vertical-align: middle;
    }
  }
`;
